import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertCircle, ExternalLink, Printer, Download, MapPin, Calendar, User, Building, Phone, Mail, Clock, CheckCircle, XCircle, AlertTriangle, DollarSign, Home, Bed, Bath, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ComplianceBadge } from './ComplianceBadge';
import { routes } from '@/lib/routes';
import { useIntake } from '@/hooks/useIntakeData';

export function IntakeDetail() {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-600">Invalid intake form ID</p>
      </div>
    );
  }

  const { data, isLoading, error, refetch, isRefetching } = useIntake(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading intake form...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-600 mb-4">Failed to load intake form</p>
        <div className="space-x-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            Try Again
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to={routes.intakes}>Back to List</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Intake form not found</p>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <Link to={routes.intakes}>Back to List</Link>
        </Button>
      </div>
    );
  }

  const getFieldLabel = (fieldKey: string) => {
    const mapping = data._meta?.fieldMapping?.[fieldKey];
    return mapping?.fieldName || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === '') return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    // Future PDF export functionality
    console.log('Export PDF functionality would be implemented here');
  };

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'in progress':
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'missing':
      case 'incomplete':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 bg-haus-paper min-h-screen print:bg-haus-paper px-4 sm:px-6">
      {/* Print Styles */}
      <style>{`
        @page {
          size: letter;
          margin: 0.5in 0.6in;
        }
        @media print {
          .no-print { display: none !important; }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: #faf9f7 !important;
          }
          .print-page {
            background: #faf9f7 !important;
            page-break-after: always;
          }
          .print-page:last-child {
            page-break-after: auto;
          }
          .haus-header {
            background: #1a1a1a !important;
            color: white !important;
          }
          .haus-gold {
            color: #b8942e !important;
          }
          .haus-accent-bg {
            background: #c8a456 !important;
          }
        }
      `}</style>

      {/* Navigation & Actions Header - Hide on print */}
      <div className="flex items-center justify-between no-print">
        <Button asChild variant="ghost" size="sm">
          <Link to={routes.intakes}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Intakes
          </Link>
        </Button>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            disabled={isRefetching}
          >
            {isRefetching ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          <Button onClick={handlePrint} variant="outline" size="sm">
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* RealtyHaus Header (Print) */}
      <div className="flex items-center justify-between mb-6 print:mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-haus-black rounded-md flex items-center justify-center haus-header">
            <span className="text-white font-bold text-sm tracking-tight">RH</span>
          </div>
          <div>
            <div className="text-xs font-semibold tracking-widest uppercase text-haus-mid">Realty Haus</div>
            <div className="text-[10px] text-haus-mid tracking-wide">Austin, Texas</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Quick Links */}
          <div className="flex items-center gap-2 text-xs no-print">
            <svg className="w-3.5 h-3.5 text-haus-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
            <a
              className="text-haus-blue underline decoration-haus-blue/30 hover:text-haus-gold transition-colors"
              href={data.flat.drive_folder_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Drive Folder
            </a>
            <span className="text-haus-light">|</span>
            <svg className="w-3.5 h-3.5 text-haus-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
            <a
              className="text-haus-blue underline decoration-haus-blue/30 hover:text-haus-gold transition-colors"
              href={`https://airtable.com/app123/tbl456/${data.flat.rec_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Airtable
            </a>
          </div>
          <div className="text-right">
            <div className="inline-block bg-haus-black text-white text-[10px] tracking-wider uppercase px-2 py-1 rounded haus-header">
              Intake Form
            </div>
            <div className="text-[10px] text-haus-mid mt-1 font-mono">
              {data.flat.rec_id || 'No ID'}
            </div>
          </div>
        </div>
      </div>

      {/* Property Hero Block */}
      <div className="bg-white rounded-lg border border-haus-light px-5 py-4 mb-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-haus-accent haus-accent-bg"></div>
        <div className="flex justify-between items-start mb-4">
          <div className="pl-3">
            <div className="text-2xl font-bold tracking-tight leading-tight text-haus-black">
              {data.flat.property_address || data.flat.full_address || data.flat.street_address || 'Property Address'}
            </div>
            {(data.flat.city || data.flat.state) && (
              <div className="text-sm text-haus-mid mt-0.5">
                {data.flat.city}{data.flat.city && data.flat.state ? ', ' : ''}{data.flat.state}{data.flat.zip ? ` ${data.flat.zip}` : ''}
              </div>
            )}
            <div className="flex items-center gap-4 mt-3">
              {data.flat.bedrooms && (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-haus-mid">Beds</span>
                    <span className="font-semibold text-sm">{data.flat.bedrooms}</span>
                  </div>
                  <div className="w-px h-3 bg-haus-light"></div>
                </>
              )}
              {data.flat.bathrooms && (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-haus-mid">Baths</span>
                    <span className="font-semibold text-sm">{data.flat.bathrooms}</span>
                  </div>
                  <div className="w-px h-3 bg-haus-light"></div>
                </>
              )}
              {data.flat.square_footage && (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-haus-mid">Sq Ft</span>
                    <span className="font-semibold text-sm">{data.flat.square_footage}</span>
                  </div>
                  <div className="w-px h-3 bg-haus-light"></div>
                </>
              )}
              {data.flat.transaction_type && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-haus-mid">Type</span>
                  <span className="font-semibold text-sm">{data.flat.transaction_type}</span>
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            {data.flat.list_price && (
              <>
                <div className="text-xs text-haus-mid mb-0.5">List Price</div>
                <div className="text-2xl font-bold text-haus-black tracking-tight">
                  ${parseInt(data.flat.list_price).toLocaleString()}
                </div>
              </>
            )}
            <div className="flex items-center gap-2 mt-2 justify-end">
              <ComplianceBadge compliance={data.compliance} size="sm" />
              {data.flat.created && (
                <span className="text-[10px] text-haus-mid font-mono">
                  {new Date(data.flat.created).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Timeline integrated into header */}
        <div className="border-t border-haus-light pt-3">
          <div className="flex gap-3">
            <div className="flex-1 bg-haus-cream/50 rounded border border-haus-light/50 px-3 py-2 text-center">
              <div className="text-[10px] text-haus-mid mb-0.5">Target Live</div>
              <div className="font-semibold text-sm">
                {(() => {
                  // Handle different possible field formats for target date
                  const targetDate = data.flat.target_live_date || data.flat.target_date || data.flat.desired_list_date;
                  if (!targetDate) return 'TBD';
                  if (Array.isArray(targetDate)) return targetDate[0] || 'TBD';
                  if (typeof targetDate === 'object' && targetDate.name) return targetDate.name;
                  return new Date(targetDate).toLocaleDateString();
                })()}
              </div>
            </div>
            <div className="flex-1 bg-haus-cream/50 rounded border border-haus-light/50 px-3 py-2 text-center">
              <div className="text-[10px] text-haus-mid mb-0.5">Status</div>
              <div className={`font-semibold text-sm ${getStatusColor(data.flat.status).includes('success') ? 'text-haus-green' : getStatusColor(data.flat.status).includes('destructive') ? 'text-haus-red' : 'text-haus-mid'}`}>
                {data.flat.status || data.flat.listing_status || 'Pending'}
              </div>
            </div>
            <div className="flex-1 bg-haus-cream/50 rounded border border-haus-light/50 px-3 py-2 text-center">
              <div className="text-[10px] text-haus-mid mb-0.5">Agent</div>
              <div className="font-semibold text-sm">
                {data.flat.listing_agent_copy || 'Agent'}
              </div>
            </div>
            <div className="flex-1 bg-haus-cream/50 rounded border border-haus-light/50 px-3 py-2 text-center">
              <div className="text-[10px] text-haus-mid mb-0.5">Client</div>
              <div className="font-semibold text-sm">
                {data.flat.full_name_from_client || 'Client'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 no-print h-12 sm:h-10">
          <TabsTrigger value="overview" className="text-sm sm:text-base">Overview</TabsTrigger>
          <TabsTrigger value="property" className="text-sm sm:text-base">Property</TabsTrigger>
          <TabsTrigger value="documents" className="text-sm sm:text-base">Documents</TabsTrigger>
          <TabsTrigger value="details" className="text-sm sm:text-base">All Details</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <>
          {/* Parties Section */}
          <div className="border-t border-haus-light pt-4">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
              Parties
            </div>
            <div className="space-y-4 sm:grid sm:grid-cols-1 md:grid-cols-2 sm:gap-4 sm:space-y-0">
              {/* Listing Agent */}
              <div className="bg-white rounded-lg border border-haus-light p-6 sm:p-4 touch-manipulation">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 sm:w-12 sm:h-12 rounded-full bg-haus-accent/20 flex items-center justify-center text-haus-gold font-bold text-lg sm:text-base border-2 border-haus-accent/30">
                    {data.flat.listing_agent_copy ? data.flat.listing_agent_copy.slice(0, 2).toUpperCase() : 'AG'}
                  </div>
                  <div>
                    <div className="font-semibold text-base sm:text-sm">
                      {data.flat.listing_agent_copy || 'Agent Name'}
                    </div>
                    <div className="text-sm sm:text-xs text-haus-mid">Listing Agent</div>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {data.flat.agent_email && (
                    <div className="flex items-center gap-2 text-xs">
                      <Mail className="w-3 h-3 text-haus-mid flex-shrink-0" />
                      <span className="text-haus-dark">{data.flat.agent_email}</span>
                    </div>
                  )}
                  {data.flat.agent_phone && (
                    <div className="flex items-center gap-2 text-xs">
                      <Phone className="w-3 h-3 text-haus-mid flex-shrink-0" />
                      <span className="text-haus-dark">{data.flat.agent_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Secondary Agent */}
              {data.flat.agent2_name && (
                <div className="bg-white rounded-lg border border-haus-light p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-haus-green/20 flex items-center justify-center text-haus-green font-bold text-lg border-2 border-haus-green/30">
                      {data.flat.agent2_name ? data.flat.agent2_name.slice(0, 2).toUpperCase() : 'SA'}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">
                        {data.flat.agent2_name}
                      </div>
                      <div className="text-xs text-haus-mid">Secondary Agent</div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {data.flat.agent2_phone && (
                      <div className="flex items-center gap-2 text-xs">
                        <Phone className="w-3 h-3 text-haus-mid flex-shrink-0" />
                        <span className="text-haus-dark">{data.flat.agent2_phone}</span>
                      </div>
                    )}
                    {data.flat.agent2_email && (
                      <div className="flex items-center gap-2 text-xs">
                        <Mail className="w-3 h-3 text-haus-mid flex-shrink-0" />
                        <span className="text-haus-dark">{data.flat.agent2_email}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Primary Client */}
              <div className="bg-white rounded-lg border border-haus-light p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-haus-blue/20 flex items-center justify-center text-haus-blue font-bold text-lg border-2 border-haus-blue/30">
                    {data.flat.full_name_from_client ? data.flat.full_name_from_client.slice(0, 2).toUpperCase() : 'CL'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold text-sm">
                        {data.flat.full_name_from_client || 'Client Name'}
                      </div>
                      <span className="inline-block bg-haus-blue/10 text-haus-blue border border-haus-blue/20 text-[9px] px-2 py-0.5 rounded font-medium">
                        Primary
                      </span>
                    </div>
                    <div className="text-xs text-haus-mid">
                      {data.flat.lead_source || 'Client'}
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {data.flat.client_email && (
                    <div className="flex items-center gap-2 text-xs">
                      <Mail className="w-3 h-3 text-haus-mid flex-shrink-0" />
                      <span className="text-haus-dark">{data.flat.client_email}</span>
                    </div>
                  )}
                  {data.flat.client_phone && (
                    <div className="flex items-center gap-2 text-xs">
                      <Phone className="w-3 h-3 text-haus-mid flex-shrink-0" />
                      <span className="text-haus-dark">{data.flat.client_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Debug secondary client fields */}
              {console.log('Secondary Client Fields:', {
                has_second_client: data.flat.has_second_client,
                client2_name: data.flat.client2_name,
                client2_relationship: data.flat.client2_relationship,
                allClientFields: Object.keys(data.flat).filter(key => key.toLowerCase().includes('client')).reduce((obj, key) => {
                  obj[key] = data.flat[key];
                  return obj;
                }, {})
              })}

              {/* Secondary Client */}
              {data.flat.has_second_client === 'Yes' && (
                <div className="bg-white rounded-lg border border-haus-light p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-bold text-lg border-2 border-orange-200">
                      {data.flat.client2_name ? data.flat.client2_name.slice(0, 2).toUpperCase() : 'C2'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-sm">
                          {data.flat.client2_name || 'Secondary Client'}
                        </div>
                        <span className="inline-block bg-orange-50 text-orange-700 border border-orange-200 text-[9px] px-2 py-0.5 rounded font-medium">
                          {data.flat.client_relationship || 'Secondary'}
                        </span>
                      </div>
                      <div className="text-xs text-haus-mid">
                        {data.flat.client_relationship || 'Secondary Client'}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {data.flat.client2_email && (
                      <div className="flex items-center gap-2 text-xs">
                        <Mail className="w-3 h-3 text-haus-mid flex-shrink-0" />
                        <span className="text-haus-dark">{data.flat.client2_email}</span>
                      </div>
                    )}
                    {data.flat.client2_phone && (
                      <div className="flex items-center gap-2 text-xs">
                        <Phone className="w-3 h-3 text-haus-mid flex-shrink-0" />
                        <span className="text-haus-dark">{data.flat.client2_phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Market Positioning - Only show for listings */}
          {data.flat.transaction_type?.toLowerCase() !== 'purchase' && (
            <div className="border-t border-haus-light pt-4">
              <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
                Market Positioning
              </div>
            <div className="bg-white rounded-lg border border-haus-light p-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Location Strength */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-haus-mid">Location Strength</span>
                    <span className="text-xs font-semibold text-haus-green">{data.flat.location_strength_from_listingco || 'TBD'}</span>
                  </div>
                  <div className="h-1.5 bg-haus-light rounded-full overflow-hidden">
                    <div className="h-full bg-haus-green rounded-full transition-all" style={{width: data.flat.location_strength_percent || '50%'}}></div>
                  </div>
                </div>
                {/* Visual Appeal */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-haus-mid">Visual Appeal</span>
                    <span className="text-xs font-semibold text-haus-green">{data.flat.visual_appeal_from_listingco || 'TBD'}</span>
                  </div>
                  <div className="h-1.5 bg-haus-light rounded-full overflow-hidden">
                    <div className="h-full bg-haus-green rounded-full transition-all" style={{width: data.flat.visual_appeal_percent || '50%'}}></div>
                  </div>
                </div>
                {/* School District */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-haus-mid">School District</span>
                    <span className="text-xs font-semibold text-haus-green">{data.flat.school_district_from_listingco || 'TBD'}</span>
                  </div>
                  <div className="h-1.5 bg-haus-light rounded-full overflow-hidden">
                    <div className="h-full bg-haus-green rounded-full transition-all" style={{width: data.flat.school_district_percent || '50%'}}></div>
                  </div>
                </div>
                {/* Empty slot for alignment */}
                <div></div>
              </div>

              {/* Buyer Type Tags */}
              <div className="mt-4 pt-3 border-t border-haus-cream">
                <div className="text-xs text-haus-mid mb-2">Buyer Type</div>
                <div className="flex flex-wrap gap-1.5">
                  {data.flat.buyer_type_from_listingco ? (
                    Array.isArray(data.flat.buyer_type_from_listingco) ?
                      data.flat.buyer_type_from_listingco.map((type, index) => (
                        <span key={index} className="inline-block bg-blue-50 text-haus-blue border border-blue-200 text-[11px] px-2 py-0.5 rounded font-medium">{type}</span>
                      )) :
                      data.flat.buyer_type_from_listingco.split(',').map((type, index) => (
                        <span key={index} className="inline-block bg-blue-50 text-haus-blue border border-blue-200 text-[11px] px-2 py-0.5 rounded font-medium">{type.trim()}</span>
                      ))
                  ) : (
                    <>
                      <span className="inline-block bg-blue-50 text-haus-blue border border-blue-200 text-[11px] px-2 py-0.5 rounded font-medium">Family Home</span>
                      <span className="inline-block bg-blue-50 text-haus-blue border border-blue-200 text-[11px] px-2 py-0.5 rounded font-medium">Turnkey</span>
                      <span className="inline-block bg-blue-50 text-haus-blue border border-blue-200 text-[11px] px-2 py-0.5 rounded font-medium">First Time Buyer</span>
                      <span className="inline-block bg-blue-50 text-haus-blue border border-blue-200 text-[11px] px-2 py-0.5 rounded font-medium">Investor</span>
                    </>
                  )}
                </div>
              </div>

              {/* Opportunity Type Tags */}
              <div className="mt-3 pt-3 border-t border-haus-cream">
                <div className="text-xs text-haus-mid mb-2">Opportunity Type</div>
                <div className="flex flex-wrap gap-1.5">
                  {data.flat.opportunity_type_from_listingco ? (
                    <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] px-2 py-0.5 rounded font-medium">{data.flat.opportunity_type_from_listingco}</span>
                  ) : (
                    <>
                      <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] px-2 py-0.5 rounded font-medium">Rental / Income Property</span>
                      <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] px-2 py-0.5 rounded font-medium">Priced Below Market</span>
                      <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] px-2 py-0.5 rounded font-medium">High ROI Potential</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Services & Inventory - Only show for listings */}
          {data.flat.transaction_type?.toLowerCase() !== 'purchase' && (
            <div className="border-t border-haus-light pt-4">
              <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
                Services & Inventory
              </div>
              <div className="flex gap-5">
              {/* Services */}
              <div className="flex-1 bg-white rounded-lg border border-haus-light p-4">
                <div className="text-xs font-semibold text-haus-dark mb-2.5">Services Requested</div>
                <div className="flex flex-wrap gap-1.5">
                  {data.flat.services ? (
                    <span className="inline-block bg-violet-50 text-violet-700 border border-violet-200 text-[11px] px-2 py-0.5 rounded font-medium">{data.flat.services}</span>
                  ) : (
                    <>
                      <span className="inline-block bg-violet-50 text-violet-700 border border-violet-200 text-[11px] px-2 py-0.5 rounded font-medium">3rd Party Photos</span>
                      <span className="inline-block bg-violet-50 text-violet-700 border border-violet-200 text-[11px] px-2 py-0.5 rounded font-medium">Zillow Showcase</span>
                    </>
                  )}
                </div>
              </div>
              {/* Inventory */}
              <div className="flex-1 bg-white rounded-lg border border-haus-light p-4">
                <div className="text-xs font-semibold text-haus-dark mb-2.5">Inventory Items</div>
                <div className="flex flex-wrap gap-1.5">
                  {data.flat.inventory_items ? (
                    data.flat.inventory_items.split(',').map((item, index) => (
                      <span key={index} className="inline-block bg-orange-50 text-orange-700 border border-orange-200 text-[11px] px-2 py-0.5 rounded font-medium">{item.trim()}</span>
                    ))
                  ) : (
                    <>
                      <span className="inline-block bg-orange-50 text-orange-700 border border-orange-200 text-[11px] px-2 py-0.5 rounded font-medium">Lockbox</span>
                      <span className="inline-block bg-orange-50 text-orange-700 border border-orange-200 text-[11px] px-2 py-0.5 rounded font-medium">Signage</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Marketing Haus */}
            <div className="mt-4">
              <div className="bg-white rounded-lg border border-haus-light p-4">
                <div className="text-xs font-semibold text-haus-dark mb-2.5">Marketing Haus</div>
                <div className="flex flex-wrap gap-1.5">
                  <span className="inline-block bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[11px] px-2 py-0.5 rounded font-medium">LD1</span>
                  <span className="inline-block bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[11px] px-2 py-0.5 rounded font-medium">LD2</span>
                  <span className="inline-block bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[11px] px-2 py-0.5 rounded font-medium">LD3</span>
                  <span className="inline-block bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[11px] px-2 py-0.5 rounded font-medium">Social Media</span>
                </div>
              </div>
            </div>
            </div>
          )}
          </>
        </TabsContent>

        {/* Property Tab */}
        <TabsContent value="property" className="space-y-6">
          {/* Property Details */}
          <div className="border-t border-haus-light pt-4">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
              Property Details
            </div>
            <div className="bg-white rounded-lg border border-haus-light p-4">
              {/* Key Features Highlight */}
              <div className="mb-4">
                <div className="text-xs font-semibold text-haus-dark mb-1.5">Features to Highlight</div>
                <div className="text-sm text-haus-dark leading-relaxed">
                  {data.flat.features_highlight || 'Property features and highlights would be displayed here'}
                </div>
              </div>

              {/* Two-column feature grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                {Object.entries(data.sections).map(([sectionName, sectionData]) => {
                  if (sectionName.toLowerCase().includes('property') || sectionName.toLowerCase().includes('building')) {
                    return Object.entries(sectionData).map(([fieldKey, value]) => (
                      <div key={`${sectionName}-${fieldKey}`} className="flex justify-between py-2 border-b border-haus-cream last:border-b-0">
                        <span className="text-xs text-haus-mid">{getFieldLabel(fieldKey)}</span>
                        <span className="text-xs font-medium text-haus-dark">{formatValue(value)}</span>
                      </div>
                    ));
                  }
                  return null;
                })}
              </div>
              </div>
            </div>

          {/* AI Property Summary */}
          <div className="border-t border-haus-light pt-4">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
              AI Property Summary
            </div>
            <div className="bg-white rounded-lg border border-haus-light p-4">
              <div className="text-xs text-haus-dark leading-relaxed space-y-2">
                <p><span className="font-semibold">Property:</span> {data.flat.full_address} — {data.flat.bedrooms || '—'} beds, {data.flat.bathrooms || '—'} baths, {data.flat.square_footage || '—'} sqft {data.flat.property_type || 'property'}.</p>
                <p><span className="font-semibold">Ideal Buyer:</span> Family Home, Turnkey, First Time Buyer, or Investor seeking a rental/income property in a good location with high ROI potential.</p>
                <p><span className="font-semibold">Highlights:</span> {data.flat.features_highlight || 'Key property features and recent updates would be highlighted here.'}</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <div className="border-t border-haus-light pt-4">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
              Document Compliance
            </div>
            <div className="bg-white rounded-lg border border-haus-light p-4">
              {/* Status Summary Bar */}
              <div className="flex items-center gap-4 mb-4 pb-3 border-b border-haus-cream">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-haus-green/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-haus-green">{data.compliance.submittedCount}</span>
                  </div>
                  <div>
                    <div className="text-[10px] text-haus-mid">Received</div>
                    <div className="text-xs font-semibold text-haus-green">Submitted</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-haus-red/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-haus-red">{data.compliance.missingCount}</span>
                  </div>
                  <div>
                    <div className="text-[10px] text-haus-mid">Outstanding</div>
                    <div className="text-xs font-semibold text-haus-red">Missing</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="flex-1 ml-4">
                  <div className="h-2 bg-haus-light rounded-full overflow-hidden">
                    <div className="h-full bg-haus-green rounded-full transition-all" style={{width: `${data.compliance.score}%`}}></div>
                  </div>
                  <div className="text-[10px] text-haus-mid mt-1 text-right">{data.compliance.submittedCount} of {data.compliance.total} documents</div>
                </div>
              </div>

              {/* Individual Document Status */}
              <div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-red-500 mr-2.5 text-sm">⭕</span>
                  <span className="text-xs text-haus-dark flex-1">Gen. Info. / Notice Buyers & Sellers</span>
                  <span className="inline-block bg-red-50 text-haus-red border border-red-200 text-[9px] px-2 py-0.5 rounded font-medium">Missing</span>
                </div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-red-500 mr-2.5 text-sm">⭕</span>
                  <span className="text-xs text-haus-dark flex-1">IABS</span>
                  <span className="inline-block bg-red-50 text-haus-red border border-red-200 text-[9px] px-2 py-0.5 rounded font-medium">Missing</span>
                </div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-red-500 mr-2.5 text-sm">⭕</span>
                  <span className="text-xs text-haus-dark flex-1">RRE Listing Agreement</span>
                  <span className="inline-block bg-red-50 text-haus-red border border-red-200 text-[9px] px-2 py-0.5 rounded font-medium">Missing</span>
                </div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-red-500 mr-2.5 text-sm">⭕</span>
                  <span className="text-xs text-haus-dark flex-1">Wire Fraud Warning</span>
                  <span className="inline-block bg-red-50 text-haus-red border border-red-200 text-[9px] px-2 py-0.5 rounded font-medium">Missing</span>
                </div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-red-500 mr-2.5 text-sm">⭕</span>
                  <span className="text-xs text-haus-dark flex-1">Disclosure of Relationship with Residential Service Co.</span>
                  <span className="inline-block bg-red-50 text-haus-red border border-red-200 text-[9px] px-2 py-0.5 rounded font-medium">Missing</span>
                </div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-red-500 mr-2.5 text-sm">⭕</span>
                  <span className="text-xs text-haus-dark flex-1">Residential Buyer Agreement</span>
                  <span className="inline-block bg-red-50 text-haus-red border border-red-200 text-[9px] px-2 py-0.5 rounded font-medium">Missing</span>
                </div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-green-600 mr-2.5 text-sm">✅</span>
                  <span className="text-xs text-haus-dark flex-1">Signature</span>
                  <span className="inline-block bg-green-50 text-haus-green border border-green-200 text-[9px] px-2 py-0.5 rounded font-medium">Received</span>
                </div>
                <div className="flex items-center py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-red-500 mr-2.5 text-sm">⭕</span>
                  <span className="text-xs text-haus-dark flex-1">Misc Docs</span>
                  <span className="inline-block bg-red-50 text-haus-red border border-red-200 text-[9px] px-2 py-0.5 rounded font-medium">Missing</span>
                </div>
              </div>

              {/* Seller Questionnaire */}
              <div className="mt-3 pt-3 border-t border-haus-cream">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 text-sm">✅</span>
                    <span className="text-xs font-medium text-haus-dark">Seller Questionnaire</span>
                  </div>
                  <span className="inline-block bg-green-50 text-haus-green border border-green-200 text-[9px] px-2 py-0.5 rounded font-medium">Completed</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTC Summary */}
          <div className="border-t border-haus-light pt-4">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
              Contracts to Close Summary
            </div>
            <div className="bg-white rounded-lg border border-haus-light p-4">
              <div className="text-xs text-haus-dark leading-relaxed space-y-1.5">
                <p><span className="font-semibold">{data.flat.agent_name || 'Agent'}</span> has submitted a new intake:</p>
                <div className="pl-3 space-y-0.5 text-haus-mid">
                  <p>Property: {data.flat.full_address || 'Property Address'}</p>
                  <p>Desired List Date: {data.flat.target_date ? new Date(data.flat.target_date).toLocaleDateString() : 'TBD'}</p>
                  <p>Services: {data.flat.services_requested || '3rd Party Photos, Zillow Showcase'}</p>
                  <p>Inventory Items: {data.flat.inventory_items || 'Lockbox, Signage'}</p>
                </div>
              </div>
            </div>
          </div>

        </TabsContent>

        {/* All Details Tab */}
        <TabsContent value="details" className="space-y-6 print:block">
          {Object.entries(data.sections).map(([sectionName, sectionData]) => (
            <div key={sectionName} className="border-t border-haus-light pt-4 print:break-inside-avoid">
              <div className="text-[10px] font-semibold tracking-widest uppercase text-haus-mid mb-3">
                {sectionName}
              </div>
              <div className="bg-white rounded-lg border border-haus-light p-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                  {Object.entries(sectionData).map(([fieldKey, value]) => (
                    <div key={fieldKey} className="flex justify-between py-2 border-b border-haus-cream last:border-b-0">
                      <span className="text-xs text-haus-mid">{getFieldLabel(fieldKey)}</span>
                      <span className="text-xs font-medium text-haus-dark text-right max-w-[160px] break-words">
                        {formatValue(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* Footer Watermark */}
      <div className="flex justify-center mt-8 print:fixed print:bottom-4 print:left-0 print:right-0">
        <div className="text-[9px] text-haus-light font-mono tracking-wider">
          HAUSFORMS.COM · CONFIDENTIAL
        </div>
      </div>

    </div>
  );
}